import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { globalVar } from '../shared/global_variables';
import { PrimitiveResult } from '../shared/models/PrimitiveResult';
import { Analysis } from './models/Analysis';
import { Folder } from './models/Folder';
import { RequestAnnualReview } from './models/RequestAnnualReview';
import { ResponseAnnualReview } from './models/ResponseAnnualReview';
import { SearchAnalysisParams } from './models/SearchAnalysisParams';
import { RequestSetSingleAnalysisInvalid } from './models/single-analysis/RequestSetSingleAnalysisInvalid';
import { ResponseSetSingleAnalysisInvalid } from './models/single-analysis/ResponseSetSingleAnalysisInvalid';
import { OperatingInstructionsDTO } from './models/OperatingInstructionsDTO';
import { AnalysisType } from './models/AnalysisType';
import { AnalysisForm } from './models/AnalysisForm';
import { AnalysisCategory } from './models/AnalysisCategory';

export const ANALYSIS = '/analysis';
export const EXPERT_MODE = '/expertMode';
export const CHOICE = '/choice';
export const ANALYSIS_SUPPLIER = '/analysisSupplier';
export const ANALYSIS_HIGHEST_VERSION = '/getHighestVersion';
export const ANALYSIS_NEW_HIGHEST_VERSION = '/getNewHighestVersion';
export const SAVE_SINGLE_ANALYSIS = '/saveSingleAnalysis';
export const VALIDATE_SINGLE_ANALYSIS = '/validateSaveSingleAnalysisValidDate';
export const FIND_ANALYSIS_BY_FUNC_KEY = '/findAnalysisByFunctionalKey';
export const FIND_CACHED_ANALYSIS_BY_FUNC_KEY = '/findCachedAnalysisByFunctionalKey';
export const FIND_ANALYSIS_BY_ID = '/findAnalysis';
export const REDIS = '/redis';
export const REDIS_UPLOAD = '/redisUpload';
export const COPY_ANALYSIS = '/copyAnalysis';
export const PASTE_ANALYSIS = '/pasteAnalysis';
export const CHANGE_HISTORY = '/changehistory';
export const IMPORT_EXPORT = '/impexp';
export const EXPORT_XLSX = '/exportXLSX';
export const REPORT = '/reporting';
export const GENERATE_REPORT = '/generateReport';
export const GENERATE_PLANNED_TVG_TIME_REPORT = '/generatePlannedTvgTimeReport';
export const GENERATE_WORD = '/generateWord';
export const RECALC_SINGLE_ANALYSIS = '/recalcSingleAnalysis';
export const RECALC_GROUP_ANALYSIS = '/recalcGroupAnalysis';
export const RECALC_MM04_FREQUENCY = '/recalcMM04Frequency';
export const RECALC_MM05_FREQUENCY = '/recalcMM05Frequency';
export const SEARCH_FOLDER = '/searchFolder';
export const SAVE_TEMPLATE = '/saveTemplate';
export const FILE = '/file';
export const UPLOAD = '/upload';
export const DELETE_FILE = '/deleteFile';
export const VIEW = '/view';
export const ANALYSIS_NEW_EMPTY_VERSION = '/getNewEmptyVersion';
export const CREATE_EMPTY_ANALYSIS = '/createEmptyAnalysis';
export const MARK_AS_ANNUALLY_REVIEWED = '/markAsAnnuallyReviewed';
export const SET_SINGLE_ANALYSIS_INVALID = '/setSingleAnalysisInvalid';
export const SAVE_GROUP_ANALYSIS = '/saveGroupAnalysis';
export const VALIDATE_GROUP_ANALYSIS = '/validateSaveGroupAnalysisValidDate';
export const STOP_SUPPLIER_HANDOVER_PROCESS = '/stopSupplierHandover';
export const SUPPLIER_HANDOVER = '/supplierHandover';
export const SUPPLIER_CONFIRM = '/supplierConfirm';
export const SUPPLIER_DECLINE = '/supplierDecline';
export const VALIDATE_DATA_MM = '/validateDataMM';
export const GET_VERSION_BY_MERGED_IMPORT = '/getVersionByMergedImport';
export const UPDATE_ANALYSIS_BY_MERGED_IMPORT = '/updateAnalysisByMergedImport';
export const SET_ANALYSIS_TO_VALID = '/setAnalysisToValid';
export const GET_ANALYSIS_TYPES = '/getAnalysisTypes';
export const GET_ANALYSIS_FORMS = '/getAnalysisForms';
export const VALIDATE_NOT_NULL = '/validateNotNull';
export const CREATE_GROUP_ANALYSIS_ELEMENTS = '/createGroupAnalysisElements';
export const UPDATE_CONNECTIONS = '/updateConnections';
export const ENUM_MME_CONSTRAINT = '/enumMMEConstraint';
export const DELETE_ANALYSIS = '/deleteAnalysis';
export const ARCHIVE_ANALYSIS = '/archiveAnalysis';

@Injectable({
    providedIn: 'root'
})
export class AnalysisService {
    constructor(private readonly httpClient: HttpClient) {}

    public getAnalysisByFuncKey(searchAnalysisParams: SearchAnalysisParams): Observable<Analysis | null> {
        let params = new HttpParams();
        if (searchAnalysisParams.analysisKey != null) {
            params = params.set('analysisKey', searchAnalysisParams.analysisKey);
        }
        if (searchAnalysisParams.version != null) {
            params = params.set('version', searchAnalysisParams.version);
        }
        return this.httpClient.get<Analysis | null>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + FIND_ANALYSIS_BY_FUNC_KEY, { params });
    }

    public getCachedAnalysisByFuncKey(searchAnalysisParams: SearchAnalysisParams): Observable<Analysis | null> {
        let params = new HttpParams();
        if (searchAnalysisParams.analysisKey != null) {
            params = params.set('analysisKey', searchAnalysisParams.analysisKey);
        }
        if (searchAnalysisParams.version != null) {
            params = params.set('version', searchAnalysisParams.version);
        }
        return this.httpClient.get<Analysis | null>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + FIND_CACHED_ANALYSIS_BY_FUNC_KEY, { params });
    }

    public getAnalysisById(id: number): Observable<Analysis> {
        return this.httpClient.get<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + FIND_ANALYSIS_BY_ID + '/' + id);
    }

    public getHighestVersion(analysis: Analysis | null): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + ANALYSIS_HIGHEST_VERSION, analysis);
    }

    public getNewHighestVersion(analysis: Analysis | null): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + ANALYSIS_NEW_HIGHEST_VERSION, analysis);
    }

    public saveAnalysisInCache(analysis: Analysis | null): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + REDIS + REDIS_UPLOAD, analysis);
    }

    public createGroupAnalysisElements(analysis: Analysis | null): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + CREATE_GROUP_ANALYSIS_ELEMENTS, analysis);
    }

    public updateConnections(analysis: Analysis | null): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + UPDATE_CONNECTIONS, analysis);
    }

    public exportChangeHistory(analysis: Analysis | null) {
        return this.httpClient.post(
            environment.baseUrl + globalVar.requestPrefix + CHANGE_HISTORY + EXPORT_XLSX,
            {
                analysis: analysis
            },
            {
                responseType: 'arraybuffer'
            }
        );
    }

    public exportAnalysisExcel(analysis: Analysis | null) {
        return this.httpClient.post(environment.baseUrl + globalVar.requestPrefix + IMPORT_EXPORT + EXPORT_XLSX, analysis, {
            responseType: 'arraybuffer'
        });
    }

    public saveSingleAnalysis(analysis: Analysis | null): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + SAVE_SINGLE_ANALYSIS, analysis);
    }

    public validateSingleAnalysis(analysis: Analysis | null): Observable<PrimitiveResult> {
        return this.httpClient.post<PrimitiveResult>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + VALIDATE_SINGLE_ANALYSIS, analysis);
    }

    public recalcSingleAnalysis(analysis: Analysis | null): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + RECALC_SINGLE_ANALYSIS, analysis);
    }

    public recalcGroupAnalysis(analysis: Analysis | null): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + RECALC_GROUP_ANALYSIS, analysis);
    }

    public recalcMM04Frequency(analysis: Analysis | null): Observable<{ data: number; datas: number | null }> {
        return this.httpClient.post<{ data: number; datas: number | null }>(
            environment.baseUrl + globalVar.requestPrefix + ANALYSIS + RECALC_MM04_FREQUENCY,
            analysis
        );
    }

    public recalcMM05Frequency(analysis: Analysis | null): Observable<{ data: number; datas: number | null }> {
        return this.httpClient.post<{ data: number; datas: number | null }>(
            environment.baseUrl + globalVar.requestPrefix + ANALYSIS + RECALC_MM05_FREQUENCY,
            analysis
        );
    }

    public printAnalysis(printOptions: {} | null): Observable<ArrayBuffer> {
        return this.httpClient.post(environment.baseUrl + globalVar.requestPrefix + REPORT + GENERATE_REPORT, printOptions, {
            responseType: 'arraybuffer'
        });
    }

    public printPlannedTvgTime(analysisKey: string, version: number, language: string): Observable<ArrayBuffer> {
        return this.httpClient.post(
            environment.baseUrl + globalVar.requestPrefix + REPORT + GENERATE_PLANNED_TVG_TIME_REPORT,
            { analysisKey, version, language },
            { responseType: 'arraybuffer' }
        );
    }

    public printOperatingInstructionsDocument(operatingInstructionsDTO: OperatingInstructionsDTO): Observable<ArrayBuffer> {
        return this.httpClient.post(environment.baseUrl + globalVar.requestPrefix + REPORT + GENERATE_WORD, operatingInstructionsDTO, {
            responseType: 'arraybuffer'
        });
    }

    public getTemplateFolders(): Observable<Folder[]> {
        return this.httpClient.post<Folder[]>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + SEARCH_FOLDER, {});
    }

    public saveTemplate(analysis: Analysis | null): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + SAVE_TEMPLATE, analysis);
    }

    public uploadFile(formData: FormData): Observable<{ data: string; datas: string[] }> {
        return this.httpClient.post<{ data: string; datas: string[] }>(environment.baseUrl + globalVar.requestPrefix + FILE + UPLOAD, formData);
    }

    public deleteFile(fileId: string) {
        return this.httpClient.delete<{ data: string; datas: string[] }>(environment.baseUrl + globalVar.requestPrefix + FILE + DELETE_FILE + '/' + fileId);
    }

    public getFile(pathContext: string) {
        return this.httpClient.get(environment.baseUrl + globalVar.requestPrefix + FILE + VIEW + '/' + pathContext, {
            responseType: 'arraybuffer'
        });
    }

    public copyAnalysis(analysis: Analysis) {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + REDIS + COPY_ANALYSIS, analysis);
    }

    public validateNotNull(analysis: Analysis): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + VALIDATE_NOT_NULL, analysis);
    }

    public pasteAnalysis(): Observable<Analysis> {
        return this.httpClient.get<Analysis>(environment.baseUrl + globalVar.requestPrefix + REDIS + PASTE_ANALYSIS);
    }

    public getVersionByMergedImport(analysis: Analysis | null): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + GET_VERSION_BY_MERGED_IMPORT, analysis);
    }

    public updateAnalysisByMergedImport(analysis: Analysis | null): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + UPDATE_ANALYSIS_BY_MERGED_IMPORT, analysis);
    }

    public getNewEmptyVersion(analysis: Analysis | null) {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + ANALYSIS_NEW_EMPTY_VERSION, analysis);
    }

    public createEmptyAnalysis(params: { analysisType: AnalysisType } | null): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + CREATE_EMPTY_ANALYSIS, params);
    }

    public markAsAnnuallyReviewed(requestAnnualReview: RequestAnnualReview) {
        return this.httpClient.post<ResponseAnnualReview>(
            environment.baseUrl + globalVar.requestPrefix + ANALYSIS + MARK_AS_ANNUALLY_REVIEWED,
            requestAnnualReview
        );
    }

    public setSingleAnalysisInvalid(params: RequestSetSingleAnalysisInvalid) {
        return this.httpClient.post<ResponseSetSingleAnalysisInvalid>(
            environment.baseUrl + globalVar.requestPrefix + ANALYSIS + SET_SINGLE_ANALYSIS_INVALID,
            params
        );
    }

    public saveGroupAnalysis(analysis: Analysis | null): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + SAVE_GROUP_ANALYSIS, analysis);
    }

    public validateGroupAnalysis(analysis: Analysis | null): Observable<PrimitiveResult> {
        return this.httpClient.post<PrimitiveResult>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + VALIDATE_GROUP_ANALYSIS, analysis);
    }

    public stopSupplierHandoverProcess(analysisId: number): Observable<Response> {
        return this.httpClient.post<Response>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS_SUPPLIER + STOP_SUPPLIER_HANDOVER_PROCESS, analysisId);
    }

    public supplierHandover(analysis: Analysis | null): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS_SUPPLIER + SUPPLIER_HANDOVER, analysis);
    }

    public supplierConfirm(analysis: Analysis | null): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS_SUPPLIER + SUPPLIER_CONFIRM, analysis);
    }

    public supplierDecline(analysis: Analysis | null): Observable<Analysis> {
        return this.httpClient.post<Analysis>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS_SUPPLIER + SUPPLIER_DECLINE, analysis);
    }

    public validateDataMM(analysis: Analysis | null): Observable<Response> {
        return this.httpClient.post<Response>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + VALIDATE_DATA_MM, analysis);
    }

    public validateSpecificDataMM(analysis: Analysis | null, criterion: string): Observable<Response> {
        return this.httpClient.post<Response>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + VALIDATE_DATA_MM + '/' + criterion, analysis);
    }

    public setAnalysisValid(analysisId: number): Observable<Analysis> {
        return this.httpClient.put<Analysis>(
            environment.baseUrl + globalVar.requestPrefix + EXPERT_MODE + SET_ANALYSIS_TO_VALID + '/' + analysisId,
            analysisId
        );
    }

    public getAnalysisTypes(category: AnalysisCategory): Observable<AnalysisType[]> {
        return this.httpClient.get<AnalysisType[]>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + GET_ANALYSIS_TYPES, { params: { category } });
    }

    public getAnalysisForms(category: AnalysisCategory): Observable<AnalysisForm[]> {
        return this.httpClient.get<AnalysisForm[]>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + GET_ANALYSIS_FORMS, { params: { category } });
    }

    public getMMEConstraints(): Observable<string[]> {
        return this.httpClient.post<string[]>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + ENUM_MME_CONSTRAINT, {});
    }

    public deleteAnalysis(analysisId: number): Observable<Response> {
        return this.httpClient.get<Response>(environment.baseUrl + globalVar.requestPrefix + ANALYSIS + DELETE_ANALYSIS + '/' + analysisId);
    }

    public archiveAnalysisManually(analysisKey: string): Observable<Record<string, string>> {
        return this.httpClient.post<Record<string, string>>(
            environment.baseUrl + globalVar.requestPrefix + ANALYSIS + ARCHIVE_ANALYSIS + '/' + analysisKey,
            null
        );
    }
}
